import React, { useState, useEffect } from "react";
import { Row, Col, Card, Modal, Button, Spinner, Tab, Nav, Table, Form } from "react-bootstrap";
import { useNavigate, useParams } from 'react-router-dom';
import MyApiClient from "../../../../api-client/MyApiClient";
import { usePageTitle } from "../../../layouts/PageHeaderExtra";
import { useStatoNelTitolo } from "../../../components/Util/StatoProdotto";
import { SidPagination } from "../../../components/Tabella/SidPagination";
import { useMaestriEImpianti } from "./datiReali";

// Stati della squadra nella tendina: Sospesa e Conclusa si vedono ma non si scelgono da qui
const STATI_SQUADRA_TENDINA = [
    { value: 'ACTIVE', label: 'Attiva', tono: 'attivo' },
    { value: 'INACTIVE', label: 'Non attiva', tono: 'non-attivo' },
    { value: 'SUSPENDED', label: 'Sospesa', tono: 'non-attivo', disabled: true },
    { value: 'COMPLETED', label: 'Conclusa', tono: 'neutro', disabled: true },
    // Archivio (TASK #7, 23/10/2026): non è uno stato del server, è Non attiva con `archived`
    { value: 'ARCHIVIATA', label: 'Archiviata', tono: 'neutro' },
];

// Righe per pagina negli elenchi di questa pagina (partecipanti, pianificazione, presenze)
const PER_PAGINA = 12;

export const CorsiSquadraDettaglio = () => {

    const { id } = useParams();
    const navigate = useNavigate();

    const [loadingShowed, setLoadingShowed] = useState(false);
    const [modalShowed, setModalShowed] = useState(false);
    const [modalTitle, setModalTitle] = useState('');
    const [modalText, setModalText] = useState('');
    const [teamData, setTeamData] = useState(null);
    // Maestro e impianto: il team porta solo gli id, i nomi si prendono dagli elenchi della struttura.
    const { maestri, impianti } = useMaestriEImpianti();
    const nomeMaestro = (t) => t.coachName || t.coach?.name || maestri.find(m => m.id === t.coachId)?.nome || '';
    const nomeImpianto = (t) => t.venueName || t.venue?.name || impianti.find(v => v.id === t.venueId)?.nome || '';
    const [members, setMembers] = useState([]);
    const [membersLoading, setMembersLoading] = useState(false);
    // Pagina corrente di pianificazione e presenze
    // In testata il nome della squadra, non la scritta «Dettaglio squadra»
    const [paginaPian, setPaginaPian] = useState(0);
    const [paginaPres, setPaginaPres] = useState(0);

    // In testata il nome della squadra, non la scritta «Dettaglio squadra»
    usePageTitle(teamData?.name || null);

    // Stato della squadra nella tendina accanto al titolo: si salva subito (regola SUI).
    // Una bozza mostra «Bozza» e si attiva dalla modifica della squadra.
    const cambiaStatoSquadra = async (nuovo) => {
        const tokenDetailsString = sessionStorage.getItem('userDetails');
        if (!tokenDetailsString) return { annullato: true };
        const token = JSON.parse(tokenDetailsString).idToken;
        const archivia = nuovo === 'ARCHIVIATA';
        const stato = archivia ? 'INACTIVE' : nuovo;
        const result = await new MyApiClient().updateCourseTeam(token, id, { status: stato, ...(archivia && { archived: true }) });
        if (result && result.error) return { error: result.error };
        setTeamData(prev => ({ ...prev, status: stato, archived: archivia }));
        return {};
    };
    useStatoNelTitolo({
        visibile: !!teamData?.status,
        bozza: teamData?.status === 'DRAFT',
        stati: STATI_SQUADRA_TENDINA,
        valore: teamData?.archived ? 'ARCHIVIATA' : teamData?.status,
        onCambia: cambiaStatoSquadra,
    });

    const [slotBookings, setSlotBookings] = useState([]);
    const [slotBookingsLoading, setSlotBookingsLoading] = useState(false);
    const [attendances, setAttendances] = useState([]);
    const [attendancesLoading, setAttendancesLoading] = useState(false);
    const [courseData, setCourseData] = useState(null);
    const [attendanceFilter, setAttendanceFilter] = useState('');
    const [memberToDelete, setMemberToDelete] = useState(null);
    const [confirmDeleteMemberShowed, setConfirmDeleteMemberShowed] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const tokenDetailsString = sessionStorage.getItem('userDetails');
                if (tokenDetailsString) {
                    const tokenDetails = JSON.parse(tokenDetailsString);
                    const token = tokenDetails.idToken;
                    const apiClient = new MyApiClient();

                    setLoadingShowed(true);
                    const courseTeams = await apiClient.getBookingCourseTeams(token);
                    setLoadingShowed(false);
                    
                    if (courseTeams && courseTeams.length > 0) {
                        // Trova il team specifico per ID
                        const team = courseTeams.find(team => team.id === id);
                        if (team) {
                            setTeamData(team);
                            // Fetch course data to check if it's OPEN
                            if (team.bookingCourseId) {
                                fetchCourseData(token, team.bookingCourseId);
                            }
                        } else {
                            setModalTitle("Errore");
                            setModalText("Team non trovato");
                            setModalShowed(true);
                        }
                    }
                }
            } catch (error) {
                console.error("Error fetching data:", error);
                setLoadingShowed(false);
                setModalTitle("Errore");
                setModalText("Errore durante il caricamento dei dati");
                setModalShowed(true);
            }
        };

        fetchData();
    }, [id]);

    const fetchCourseData = async (token, courseId) => {
        try {
            const apiClient = new MyApiClient();
            const course = await apiClient.getBookingCourseById(token, courseId);
            setCourseData(course);
        } catch (error) {
            console.error('Error fetching course data:', error);
        }
    };

    const fetchMembers = async () => {
        setMembersLoading(true);
        try {
            const tokenDetailsString = sessionStorage.getItem('userDetails');
            if (tokenDetailsString) {
                const tokenDetails = JSON.parse(tokenDetailsString);
                const token = tokenDetails.idToken;
                const apiClient = new MyApiClient();
                const response = await apiClient.getBookingCourseTeamMembers(token, id);
                setMembers(Array.isArray(response) ? response : []);
            }
        } catch (error) {
            console.error('Error fetching members:', error);
            setModalTitle("Errore");
            setModalText("Errore nel caricamento dei membri");
            setModalShowed(true);
        } finally {
            setMembersLoading(false);
        }
    };

    // Gli acquisti del corso: da qui arrivano email, telefono e stato del pagamento, che
    // l'elenco dei membri della squadra non porta.
    const [acquisti, setAcquisti] = useState([]);
    useEffect(() => {
        const leggi = async () => {
            if (!teamData?.bookingCourseId) return;
            try {
                const tokenDetailsString = sessionStorage.getItem('userDetails');
                if (!tokenDetailsString) return;
                const token = JSON.parse(tokenDetailsString).idToken;
                const risposta = await new MyApiClient().getUserBookingCourses(token, teamData.bookingCourseId);
                setAcquisti(Array.isArray(risposta) ? risposta : []);
            } catch (errore) {
                console.error('Acquisti del corso non letti', errore);
            }
        };
        leggi();
    }, [teamData?.bookingCourseId]);

    // Ricerca e pagine dell'elenco partecipanti
    const [ricerca, setRicerca] = useState('');
    const [paginaPart, setPaginaPart] = useState(0);

    // Se l'API risponde con un errore invece che con un elenco, qui non deve saltare niente
    const elencoMembri = Array.isArray(members) ? members : [];
    const partecipanti = elencoMembri.map(m => {
        const acquisto = acquisti.find(a => String(a.juniorId || a.playerId) === String(m.userId));
        return {
            userId: m.userId,
            userType: m.userType,
            nome: `${m.firstName || ''} ${m.lastName || ''}`.trim() || acquisto?.juniorName || acquisto?.playerName || 'n.d.',
            // Contatti dal profilo IDS del partecipante (per un junior, del genitore); l'acquisto e' la riserva
            email: m.email || acquisto?.email || '',
            telefono: m.phoneNumber || acquisto?.phone || '',
            pagato: acquisto ? acquisto.status === 'PAID' : null,
        };
    });
    const cercato = ricerca.trim().toLowerCase();
    const partecipantiFiltrati = partecipanti.filter(p =>
        !cercato || [p.nome, p.email, p.telefono].some(v => (v || '').toLowerCase().includes(cercato)));
    const paginePart = Math.max(1, Math.ceil(partecipantiFiltrati.length / PER_PAGINA));
    const partCorrente = Math.min(paginaPart, paginePart - 1);
    const partDellaPagina = partecipantiFiltrati.slice(partCorrente * PER_PAGINA, (partCorrente + 1) * PER_PAGINA);

    // I partecipanti si leggono all'apertura della pagina: prima arrivavano solo cliccando
    // il TAB, e con il TAB rinominato non si leggevano più.
    useEffect(() => {
        if (id) fetchMembers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const fetchSlotBookings = async () => {
        if (!id) return;
        
        setSlotBookingsLoading(true);
        try {
            const tokenDetailsString = sessionStorage.getItem('userDetails');
            if (tokenDetailsString) {
                const tokenDetails = JSON.parse(tokenDetailsString);
                const token = tokenDetails.idToken;
                const apiClient = new MyApiClient();
                
                const slotBookingsData = await apiClient.getTeamSlotBookings(token, id);
                setSlotBookings(slotBookingsData || []);
            }
        } catch (error) {
            console.error('Error fetching slot bookings:', error);
            setModalTitle("Errore");
            setModalText("Errore nel caricamento della pianificazione");
            setModalShowed(true);
        } finally {
            setSlotBookingsLoading(false);
        }
    };

    const fetchAttendances = async () => {
        if (!id) return;
        
        setAttendancesLoading(true);
        try {
            const tokenDetailsString = sessionStorage.getItem('userDetails');
            if (tokenDetailsString) {
                const tokenDetails = JSON.parse(tokenDetailsString);
                const token = tokenDetails.idToken;
                const apiClient = new MyApiClient();
                
                const attendancesData = await apiClient.getTeamAttendances(token, id);
                setAttendances(attendancesData || []);
            }
        } catch (error) {
            console.error('Error fetching attendances:', error);
            setModalTitle("Errore");
            setModalText("Errore nel caricamento delle presenze");
            setModalShowed(true);
        } finally {
            setAttendancesLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('it-IT');
    };

    const formatDateTime = (timestamp) => {
        if (!timestamp) return '';
        const date = new Date(timestamp);
        return date.toLocaleString('it-IT', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getDayOfWeek = (timestamp) => {
        if (!timestamp) return '';
        const date = new Date(timestamp);
        const days = ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'];
        return days[date.getDay()];
    };

    const getStatusBadge = (status) => {
        const badgeClass = status === 'PRESENT' ? 'badge-success' : 
                          status === 'ABSENT' ? 'badge-danger' : 
                          'badge-secondary';
        const statusText = status === 'PRESENT' ? 'Presente' : 
                          status === 'ABSENT' ? 'Assente' : 
                          status;
        return <span className={`badge ${badgeClass}`}>{statusText}</span>;
    };

    const getUserTypeBadge = (userType) => {
        const badgeClass = userType === 'JUNIOR' ? 'badge-info' :
                          (userType === 'IDS_PLAY' || userType === 'PLAYER' || userType === 'IDS_GURU') ? 'badge-primary' :
                          'badge-secondary';
        const typeText = userType === 'JUNIOR' ? 'Junior' :
                        (userType === 'IDS_PLAY' || userType === 'PLAYER' || userType === 'IDS_GURU') ? 'Player' :
                        userType;
        return <span className={`badge ${badgeClass}`}>{typeText}</span>;
    };









    // Pagina di sola consultazione: i campi si leggono e basta, come gli altri qui dentro.
    const renderEditableField = (fieldName, label, type = 'text') => {
        const value = teamData[fieldName];
        const bloccato = { color: 'rgba(108,117,125,0.6)', backgroundColor: '#f2f2f2', paddingLeft: '15px', fontWeight: 'normal', fontSize: '14px' };
        return (
            <div>
                <Form.Label className="mb-2">{label}</Form.Label>
                {type === 'textarea' ? (
                    <Form.Control disabled as="textarea" rows={4} className="mt-2" style={bloccato} value={value || ''} readOnly />
                ) : (
                    <Form.Control disabled type={type} className="mt-2" style={bloccato} value={(value || value === 0) ? value : ''} readOnly />
                )}
            </div>
        );
    };



    const confirmRemoveMember = async () => {
        if (!memberToDelete) return;
        setConfirmDeleteMemberShowed(false);
        try {
            const tokenDetailsString = sessionStorage.getItem('userDetails');
            if (tokenDetailsString) {
                const tokenDetails = JSON.parse(tokenDetailsString);
                const token = tokenDetails.idToken;
                const apiClient = new MyApiClient();
                setLoadingShowed(true);
                const result = await apiClient.removeMemberFromTeam(token, id, memberToDelete.userId);
                setLoadingShowed(false);
                if (result === true) {
                    setMembers(prev => prev.filter(m => m.userId !== memberToDelete.userId));
                } else {
                    setModalTitle("Errore");
                    setModalText(result?.error || "Errore durante la rimozione");
                    setModalShowed(true);
                }
            }
        } catch (error) {
            setLoadingShowed(false);
            setModalTitle("Errore");
            setModalText("Errore durante la rimozione del membro");
            setModalShowed(true);
        } finally {
            setMemberToDelete(null);
        }
    };

    if (!teamData) {
        return (
            <>
                <Modal className="fade" show={loadingShowed} centered>
                    <Modal.Body className="text-center">
                        <Spinner animation="border" role="status">
                            <span className="sr-only">Loading...</span>
                        </Spinner>
                        <div>Caricamento in corso...</div>
                    </Modal.Body>
                </Modal>

                <Modal className="fade" show={modalShowed} centered>
                    <Modal.Header>
                        <Modal.Title>{modalTitle}</Modal.Title>
                        <Button onClick={() => setModalShowed(false)} variant="" className="btn-close">
                        </Button>
                    </Modal.Header>
                    <Modal.Body>
                        <Row><Col className="col-xl-64">{modalText}</Col></Row>
                    </Modal.Body>
                    <Modal.Footer className="justify-content-center">
                        <Button className="btn btn-primary btn-add" onClick={() => setModalShowed(false)}>
                            Chiudi
                        </Button>
                    </Modal.Footer>
                </Modal>
            </>
        );
    }

    // Solo consultazione: nome e cognome, come nell'elenco partecipanti del corso.
    // Pianificazione e presenze possono essere lunghe: si sfogliano a pagine.
    const presenzeFiltrate = attendances.filter(a => !attendanceFilter || (a.playerName || '').toLowerCase().includes(attendanceFilter.toLowerCase()));
    const paginePian = Math.max(1, Math.ceil(slotBookings.length / PER_PAGINA));
    const paginePres = Math.max(1, Math.ceil(presenzeFiltrate.length / PER_PAGINA));
    const pianCorrente = Math.min(paginaPian, paginePian - 1);
    const presCorrente = Math.min(paginaPres, paginePres - 1);
    const pianDellaPagina = slotBookings.slice(pianCorrente * PER_PAGINA, (pianCorrente + 1) * PER_PAGINA);
    const presDellaPagina = presenzeFiltrate.slice(presCorrente * PER_PAGINA, (presCorrente + 1) * PER_PAGINA);

    const piedeElenco = (quante, etichetta, pagina, pagine, vaiA) => (
        <div className="d-flex align-items-center justify-content-between footTab">
            <span className="sid-list-total">{quante === 1 ? `1 ${etichetta}` : `${quante} ${etichetta}`}</span>
            <SidPagination
                pageIndex={pagina}
                pageCount={pagine}
                gotoPage={vaiA}
                previousPage={() => vaiA(Math.max(0, pagina - 1))}
                nextPage={() => vaiA(Math.min(pagine - 1, pagina + 1))}
                canPreviousPage={pagina > 0}
                canNextPage={pagina < pagine - 1}
            />
        </div>
    );


    const tabData = [
        {
            id: "informazioni",
            name: "Informazioni generali",
            content: (
                <Card>
                    <Card.Header><h4 className="card-title">Informazioni generali Squadra</h4></Card.Header>
                    <Card.Body>
                        <Row>
                            <Col className="col-xl-6">
                                <div className="mb-3">
                                    {renderEditableField('name', 'Nome Squadra', 'text')}
                                </div>
                            </Col>
                            <Col className="col-xl-6">
                                <div className="mb-3">
                                    <Form.Label>Corso associato</Form.Label>
                                    {/* Il nome del corso apre la sua scheda: da qui si torna alle altre squadre,
                                        ai partecipanti e alla programmazione del corso. */}
                                    <div className="mt-2">
                                        {teamData.bookingCourseId ? (
                                            <span className="sid-cell-link" role="button" tabIndex={0}
                                                title="Apri la scheda del corso"
                                                style={{ fontSize: '18px', fontWeight: 700, textDecoration: 'underline', textUnderlineOffset: '3px' }}
                                                onClick={() => navigate(`/struttura/corsi/${teamData.bookingCourseId}`)}
                                                onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/struttura/corsi/${teamData.bookingCourseId}`); }}>
                                                {courseData?.name || 'Apri il corso'} ›
                                            </span>
                                        ) : (
                                            <div className="sid-readonly-value">{courseData?.name || '—'}</div>
                                        )}
                                    </div>
                                </div>
                            </Col>
                        </Row>
                        <Row className="mt-4">
                            <Col className="col-xl-6">
                                <div className="mb-3">
                                    <Form.Label>Numero partecipanti</Form.Label>
                                    <Form.Control disabled className="mt-2" style={{color: 'rgba(108,117,125,0.6)', backgroundColor: '#f2f2f2', paddingLeft: '15px', fontWeight: 'normal', fontSize: '14px'}} type="text" value={`${teamData.membersIds.length} / ${teamData.maxMembers}`} />
                                </div>
                            </Col>
                        </Row>
                        <Row className="mt-4">
                            <Col className="col-xl-6">
                                <div className="mb-3">
                                    {renderEditableField('maxMembers', 'Massimo partecipanti', 'number')}
                                </div>
                            </Col>
                        </Row>
                        <Row className="mt-4">
                            <Col className="col-xl-6">
                                <div className="mb-3">
                                    <Form.Label>Data Inizio</Form.Label>
                                    <Form.Control disabled className="mt-2" style={{color: 'rgba(108,117,125,0.6)', backgroundColor: '#f2f2f2', paddingLeft: '15px', fontWeight: 'normal', fontSize: '14px'}} type="text" value={formatDate(teamData.startDate) || ''} />
                                </div>
                            </Col>
                            <Col className="col-xl-6">
                                <div className="mb-3">
                                    <Form.Label>Data Fine</Form.Label>
                                    <Form.Control disabled className="mt-2" style={{color: 'rgba(108,117,125,0.6)', backgroundColor: '#f2f2f2', paddingLeft: '15px', fontWeight: 'normal', fontSize: '14px'}} type="text" value={formatDate(teamData.endDate) || ''} />
                                </div>
                            </Col>
                        </Row>
                        <Row className="mt-4">
                            <Col className="col-xl-6">
                                <div className="mb-3">
                                    <Form.Label>Maestro</Form.Label>
                                    <div className="sid-readonly-value mt-2">{nomeMaestro(teamData) || '—'}</div>
                                </div>
                            </Col>
                            <Col className="col-xl-6">
                                <div className="mb-3">
                                    <Form.Label>Impianto</Form.Label>
                                    <div className="sid-readonly-value mt-2">{nomeImpianto(teamData) || '—'}</div>
                                </div>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>
            )
        },
        {
            id: "partecipanti",
            name: "Partecipanti",
            content: (
                <Card>
                    <Card.Body className="sid-card-body--spaced">
                        <div className="d-flex justify-content-end mb-3">
                            <Button className="btn btn-primary btn-sm btn-add"
                                onClick={() => navigate(`/struttura/corsi/${teamData.bookingCourseId || ''}/partecipanti/nuovo`)}>
                                <span className="ms-2">Nuovo partecipante</span>
                            </Button>
                        </div>

                        <div style={{ maxWidth: 360 }}>
                            <Form.Label className="mb-2">Cerca per nome, email o telefono</Form.Label>
                            <Form.Control className="mt-2" value={ricerca} onChange={(e) => { setRicerca(e.target.value); setPaginaPart(0); }} />
                        </div>

                        <div className="table-responsive mt-3">
                            <table className="table dataTable display">
                                <thead>
                                    <tr>
                                        <th>Nome</th>
                                        <th>Email</th>
                                        <th>Telefono</th>
                                        <th>Pagamento</th>
                                        <th>Tipo</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {membersLoading ? (
                                        <tr><td colSpan={5} className="text-center py-4">Caricamento…</td></tr>
                                    ) : partDellaPagina.length === 0 ? (
                                        <tr><td colSpan={5} className="text-center text-muted py-4">Nessun partecipante in questa squadra.</td></tr>
                                    ) : partDellaPagina.map(p => (
                                        <tr key={p.userId} className="sid-row-link" role="button" tabIndex={0}
                                            onClick={() => navigate(p.userType === 'JUNIOR'
                                                ? `/struttura/junior/junior_profile/${p.userId}`
                                                : `/struttura/clienti/player_profile/${p.userId}`)}>
                                            <td className="sid-row-link__name">{p.nome}</td>
                                            <td>{p.email || '—'}</td>
                                            <td>{p.telefono || '—'}</td>
                                            <td>
                                                {/* Solo lo stato del pagamento: Confermato o In attesa */}
                                                {p.pagato === true && <span className="sid-chip-pagato">Confermato</span>}
                                                {p.pagato === false && <span className="sid-chip-attesa">In attesa</span>}
                                                {p.pagato === null && '—'}
                                            </td>
                                            <td>{getUserTypeBadge(p.userType)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {piedeElenco(partecipantiFiltrati.length, partecipantiFiltrati.length === 1 ? 'partecipante' : 'partecipanti', partCorrente, paginePart, setPaginaPart)}
                    </Card.Body>
                </Card>
            )
        },
        {
            id: "pianificazione",
            name: "Pianificazione",
            content: (
                <Card>
                    <Card.Header><h4 className="card-title">Pianificazione attività della squadra</h4></Card.Header>
                    <Card.Body>
                        {slotBookingsLoading ? (
                            <div className="text-center">
                                <Spinner animation="border" role="status">
                                    <span className="sr-only">Loading...</span>
                                </Spinner>
                                <div>Caricamento slot bookings...</div>
                            </div>
                        ) : (
                            <Table responsive>
                                <thead>
                                    <tr>
                                        <th>Giorno</th>
                                        <th>Data Inizio</th>
                                        <th>Data Fine</th>
                                        <th>Stato</th>
                                        <th>Corso</th>
                                        <th>Staff tecnico</th>
                                        <th>Impianto</th>
                                        <th>Note</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {slotBookings.length === 0 ? (
                                        <tr>
                                            <td colSpan="8" className="text-center">{teamData.status === 'DRAFT' ? "La squadra è in bozza: le sedute vanno a calendario quando la si attiva." : "Nessuna seduta a calendario."}</td>
                                        </tr>
                                    ) : (
                                        pianDellaPagina.map((booking, index) => (
                                            <tr key={booking.id || index}>
                                                <td><strong>{getDayOfWeek(booking.startDateTime)}</strong></td>
                                                <td>{formatDateTime(booking.startDateTime)}</td>
                                                <td>{formatDateTime(booking.endDateTime)}</td>
                                                <td>
                                                    <span className={`badge ${
                                                        booking.status === 'CONFIRMED' ? 'badge-success' :
                                                        booking.status === 'PENDING' ? 'badge-warning' :
                                                        booking.status === 'CANCELLED' ? 'badge-danger' :
                                                        'badge-secondary'
                                                    }`}>
                                                        {booking.status === 'CONFIRMED' ? 'Confermato' :
                                                         booking.status === 'PENDING' ? 'In attesa' :
                                                         booking.status === 'CANCELLED' ? 'Annullato' :
                                                         booking.status}
                                                    </span>
                                                </td>
                                                <td>{booking.courseName || 'N/A'}</td>
                                                <td>{booking.coachName || '—'}</td>
                                                <td>{booking.venueName || '—'}</td>
                                                <td>{booking.notes || 'N/A'}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </Table>
                        )}
                            {slotBookings.length > 0 && piedeElenco(slotBookings.length, slotBookings.length === 1 ? 'seduta' : 'sedute', pianCorrente, paginePian, setPaginaPian)}
                    </Card.Body>
                </Card>
            )
        },
        {
            id: "presenze",
            name: "Presenze",
            content: (
                <Card>
                    <Card.Header>
                        <div className="d-flex justify-content-between align-items-center w-100">
                            <h4 className="card-title mb-0">Presenze della squadra</h4>
                            <div className="ms-auto">
                                <Form.Control
                                    type="text"
                                    placeholder="Filtra per membro..."
                                    value={attendanceFilter}
                                    onChange={e => setAttendanceFilter(e.target.value)}
                                    style={{ maxWidth: 240 }}
                                    size="sm"
                                />
                            </div>
                        </div>
                    </Card.Header>
                    <Card.Body>
                        {attendancesLoading ? (
                            <div className="text-center">
                                <Spinner animation="border" role="status">
                                    <span className="sr-only">Loading...</span>
                                </Spinner>
                                <div>Caricamento presenze...</div>
                            </div>
                        ) : (
                            <Table responsive>
                                <thead>
                                    <tr>
                                        <th>Giorno</th>
                                        <th>Data Inizio</th>
                                        <th>Data Fine</th>
                                        <th>Player</th>
                                        <th>Staff tecnico</th>
                                        <th>Stato</th>
                                        <th>Inserimento</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {attendances.length === 0 ? (
                                        <tr>
                                            <td colSpan="7" className="text-center">Nessuna presenza trovata</td>
                                        </tr>
                                    ) : (
                                        presDellaPagina.map((attendance, index) => (
                                            <tr key={attendance.id || index}>
                                                <td><strong>{getDayOfWeek(attendance.activityStartDateTime || attendance.startDateTime)}</strong></td>
                                                <td>{formatDateTime(attendance.activityStartDateTime || attendance.startDateTime)}</td>
                                                <td>{formatDateTime(attendance.activityEndDateTime || attendance.endDateTime)}</td>
                                                <td>
                                                    <div>
                                                        {attendance.playerName}
                                                        <div className="mt-1">
                                                            {getUserTypeBadge(attendance.userType)}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>{attendance.coachName || '—'}</td>
                                                <td>{getStatusBadge(attendance.status)}</td>
                                                <td>{formatDateTime(attendance.createdAt)}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </Table>
                        )}
                            {presenzeFiltrate.length > 0 && piedeElenco(presenzeFiltrate.length, presenzeFiltrate.length === 1 ? 'presenza' : 'presenze', presCorrente, paginePres, setPaginaPres)}
                    </Card.Body>
                </Card>
            )
        }
    ];

    return (
        <>
            <Modal className="fade" show={modalShowed} centered>
                <Modal.Header>
                    <Modal.Title>{modalTitle}</Modal.Title>
                    <Button onClick={() => setModalShowed(false)} variant="" className="btn-close">
                    </Button>
                </Modal.Header>
                <Modal.Body>
                    <Row><Col className="col-xl-64">{modalText}</Col></Row>
                </Modal.Body>
                <Modal.Footer className="justify-content-center">
                    <Button className="btn btn-primary btn-add" onClick={() => setModalShowed(false)}>
                        Chiudi
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal className="fade" show={loadingShowed} centered>
                <Modal.Body className="text-center">
                    <Spinner animation="border" role="status">
                        <span className="sr-only">Loading...</span>
                    </Spinner>
                    <div>Caricamento in corso...</div>
                </Modal.Body>
            </Modal>

            <Row className="align-items-start mt-4">
                <div className="col-xl-12">

                    <div className="custom-tab-1">
                        <Tab.Container defaultActiveKey="informazioni">
                            <Nav as="ul" className="nav-tabs" style={{ border: 'none' }}>
                                {tabData.map((data, i) => (
                                    <Nav.Item as="li" key={i} className="tabMenu">
                                        <Nav.Link 
                                            eventKey={data.id.toLowerCase()}
                                            onClick={data.id === 'partecipanti' ? fetchMembers : data.id === 'pianificazione' ? fetchSlotBookings : data.id === 'presenze' ? fetchAttendances : undefined}
                                        >
                                            {data.name}
                                        </Nav.Link>
                                    </Nav.Item>
                                ))}
                            </Nav>
                            <Row className="align-items-start mt-4">
                                <div className="col-12">
                                    <Tab.Content>
                                        {tabData.map((data, i) => (
                                            <Tab.Pane eventKey={data.id.toLowerCase()} key={i}>
                                                {data.content}
                                            </Tab.Pane>
                                        ))}
                                    </Tab.Content>
                                </div>
                            </Row>
                        </Tab.Container>
                    </div>
                </div>
            </Row>

            <Modal className="fade" show={confirmDeleteMemberShowed} centered>
                <Modal.Header>
                    <Modal.Title>Rimuovere il partecipante?</Modal.Title>
                    <Button onClick={() => setConfirmDeleteMemberShowed(false)} variant="" className="btn-close"></Button>
                </Modal.Header>
                <Modal.Body>
                    <Row><Col><strong>{memberToDelete?.firstName} {memberToDelete?.lastName}</strong> esce dalla squadra.</Col></Row>
                </Modal.Body>
                <Modal.Footer className="justify-content-center sid-footer-conferma">
                    <Button className="btn btn-light btn-add" onClick={() => setConfirmDeleteMemberShowed(false)}>Annulla</Button>
                    <Button className="btn btn-danger btn-add" onClick={confirmRemoveMember}>Rimuovi</Button>
                </Modal.Footer>
            </Modal>

        </>
    );
};

export default CorsiSquadraDettaglio;